import stackData from '../data/stackDependence.json';

/**
 * DependenceParityChart — the framework from Han/Jiang/Mei, drawn (Fig 2).
 *
 * A GEOMETRY diagram of the published method, not a plot of measurements, so it
 * is drawn as responsive SVG with computed coordinates rather than handed to a
 * chart engine (no axis auto-layout to fight, and one message to carry).
 *
 * P is an illustrative position: the caption and the aria-label both say so, so
 * the point can never be read as a measured year.
 */

const W = 520;
const H = 420;
const PAD = { left: 74, right: 40, top: 28, bottom: 62 };

const px = (v: number) => PAD.left + v * (W - PAD.left - PAD.right);
const py = (v: number) => H - PAD.bottom - v * (H - PAD.top - PAD.bottom);

const P: [number, number] = [0.26, 0.7];
const Q: [number, number] = [(P[0] + P[1]) / 2, (P[0] + P[1]) / 2];

const ORANGE = '#FD9800';
const MUTED = '#9CA3AF';
const SOFT = '#6B7280';

export const DependenceParityChart = () => {
  const fig = (
    stackData as unknown as {
      figures: { parity_framework: { note: string } };
    }
  ).figures.parity_framework;

  return (
    <figure className="my-8 rounded-xl overflow-hidden border border-[#333] bg-[#171717] p-3">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Framework diagram: U.S. versus Chinese patent citation propensities, the 45 degree parity line, and an illustrative point P projected onto it. Illustrative only — no measured years plotted."
        className="w-full h-auto"
      >
        <rect width={W} height={H} fill="#171717" />

        {/* Plot frame */}
        <rect
          x={PAD.left}
          y={PAD.top}
          width={W - PAD.left - PAD.right}
          height={H - PAD.top - PAD.bottom}
          fill="#1b1b1b"
          stroke="#333"
        />

        {/* Region tints */}
        <polygon
          points={`${px(0)},${py(0)} ${px(1)},${py(1)} ${px(0)},${py(1)}`}
          fill={ORANGE}
          opacity="0.06"
        />

        {/* Parity line */}
        <line
          x1={px(0)}
          y1={py(0)}
          x2={px(1)}
          y2={py(1)}
          stroke={ORANGE}
          strokeWidth="2"
          strokeDasharray="7 5"
        />
        <text
          x={px(0.62)}
          y={py(0.62) - 14}
          fill={ORANGE}
          fontSize="11"
          fontWeight="700"
          textAnchor="end"
        >
          45° parity line
        </text>

        {/* Rejection from parity (dependence) and projection onto it (integration) */}
        <line x1={px(P[0])} y1={py(P[1])} x2={px(Q[0])} y2={py(Q[1])} stroke={MUTED} strokeWidth="1.5" />
        <line x1={px(Q[0])} y1={py(Q[1])} x2={px(1)} y2={py(1)} stroke={SOFT} strokeWidth="1.5" strokeDasharray="3 3" />

        {/* Points */}
        <circle cx={px(P[0])} cy={py(P[1])} r="5.5" fill={ORANGE} />
        <circle cx={px(Q[0])} cy={py(Q[1])} r="4.5" fill="#e5e5e5" />
        <circle cx={px(0)} cy={py(0)} r="3.5" fill={SOFT} />
        <circle cx={px(1)} cy={py(1)} r="3.5" fill="#e5e5e5" />

        {/* Point labels */}
        <text x={px(P[0]) + 10} y={py(P[1]) - 6} fill="#f5f5f5" fontSize="13" fontWeight="700">
          P
        </text>
        <text x={px(Q[0]) - 20} y={py(Q[1]) + 16} fill="#f5f5f5" fontSize="13" fontWeight="700">
          Q
        </text>
        <text x={px(1) - 14} y={py(1) - 10} fill={MUTED} fontSize="11">
          I
        </text>

        {/* Region labels, kept clear of the marks */}
        <text x={px(0.06)} y={py(0.93)} fill={ORANGE} fontSize="11.5" fontWeight="700">
          U.S.-leading
        </text>
        <text x={px(0.06)} y={py(0.93) + 15} fill={MUTED} fontSize="10.5">
          China leans on U.S. knowledge
        </text>
        <text x={px(0.55)} y={py(0.14)} fill={MUTED} fontSize="11.5" fontWeight="700">
          China-leading
        </text>
        <text x={px(0.06)} y={py(0.02) + 20} fill={SOFT} fontSize="10">
          complete decoupling
        </text>
        <text x={px(1) - 12} y={py(1) + 28} fill={SOFT} fontSize="10" textAnchor="end">
          complete integration
        </text>

        {/* Axis titles */}
        <text x={(px(0) + px(1)) / 2} y={H - 18} fill={MUTED} fontSize="11" textAnchor="middle">
          U.S. patents citing Chinese, relative to citing U.S.  (p u,c)
        </text>
        <text
          transform={`translate(20 ${(py(0) + py(1)) / 2}) rotate(-90)`}
          fill={MUTED}
          fontSize="11"
          textAnchor="middle"
        >
          Chinese patents citing U.S., relative to citing Chinese  (p c,u)
        </text>

        {/* Vector legend */}
        <line x1={px(0.06)} y1={py(0.3)} x2={px(0.06) + 26} y2={py(0.3)} stroke={MUTED} strokeWidth="1.5" />
        <text x={px(0.06) + 34} y={py(0.3) + 4} fill={MUTED} fontSize="10.5">
          PQ: distance from parity = dependence
        </text>
        <line
          x1={px(0.06)}
          y1={py(0.22)}
          x2={px(0.06) + 26}
          y2={py(0.22)}
          stroke={SOFT}
          strokeWidth="1.5"
          strokeDasharray="3 3"
        />
        <text x={px(0.06) + 34} y={py(0.22) + 4} fill={SOFT} fontSize="10.5">
          along the line = integration
        </text>
      </svg>
      <figcaption className="mt-2 text-xs text-gray-400 text-center px-4">
        Figure 2. How the published index is read. Above the parity line, Chinese patents build on U.S. patents
        more than the reverse; the orthogonal distance from the line measures dependence, the projection along it
        measures integration. P is illustrative of the reading — no measured year is plotted. {fig.note}
      </figcaption>
    </figure>
  );
};
